/**
 * Website / marketing color tokens.
 *
 * Neutrals + semantics match the product design system ramps.
 * Sales/site primary stays cobalt; product UI uses sapphire separately.
 *
 * Semantics (same as app): emerald ↑ / ruby (rose) ↓ / honey pending.
 */
export const SLATE = {
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a'
} as const;

/** Positive — emerald (product `primitives.emerald`). */
export const EMERALD = {
  50: '#e7f5ef',
  100: '#d3ede2',
  200: '#bfe5d5',
  300: '#8fd4b6',
  400: '#3aab84',
  500: '#169970',
  600: '#0b8a63',
  700: '#087a57',
  800: '#076b4e',
  900: '#054d38'
} as const;

/**
 * Negative — rose in product tokens; “Ruby” in marketing/decks.
 * Same hexes as `primitives.rose`.
 */
export const ROSE = {
  50: '#fbecf1',
  100: '#f7dae4',
  200: '#f3c8d6',
  300: '#e898b5',
  400: '#de6f95',
  500: '#d14f7b',
  600: '#c73562',
  700: '#9c2a4d',
  800: '#7d2340',
  900: '#5c1a30'
} as const;

export const RUBY = ROSE;

/** Warning / pending — honey (product `primitives.honey`). */
export const HONEY = {
  50: '#faf3dc',
  100: '#f3e6be',
  200: '#ecd9a0',
  300: '#dfbd5e',
  400: '#d4a92b',
  500: '#c4941c',
  600: '#b7881c',
  700: '#8a660a',
  800: '#6d5008',
  900: '#4f3a05'
} as const;

/** Role tokens for decks — mirrors product `light.positive|negative|warning`. */
export const SEMANTIC = {
  positive: {
    main: EMERALD[600],
    text: EMERALD[800],
    surface: EMERALD[50],
    border: EMERALD[200]
  },
  negative: {
    main: ROSE[600],
    text: ROSE[700],
    surface: ROSE[50],
    border: ROSE[200]
  },
  /** Alias of negative for sales language. */
  ruby: {
    main: ROSE[600],
    text: ROSE[700],
    surface: ROSE[50],
    border: ROSE[200]
  },
  warning: {
    main: HONEY[600],
    text: HONEY[700],
    surface: HONEY[50],
    border: HONEY[200]
  }
} as const;

export const COLORS = {
  WHITE: '#ffffff',
  /** Cool canvas — was warm stone #fafaf9; align with slate 50. */
  PAPER: SLATE[50],
  PAPER_DARK: SLATE[100],
  TEXT: '#1a1a1a',
  /** Cool slate — was warm #4f4f4f; matches product text.secondary. */
  TEXT_SECONDARY: SLATE[600],
  /** Quieter supporting copy / hints — slate 500. */
  TEXT_MUTED: SLATE[500],
  INK: '#0e1c2b',
  PRIMARY: '#2e5cff',
  PRIMARY_LIGHT: '#7fa3ff',
  PRIMARY_DARK: '#1e44d8',
  PRIMARY_SURFACE: '#eef3ff',
  PRIMARY_BORDER: '#d4e0ff',
  /** @deprecated Prefer SLATE.* — kept as aliases for existing call sites. */
  GRAY: {
    surface: SLATE[50],
    surfaceTint: '#f4f8fc',
    borderLight: SLATE[100],
    border: SLATE[200],
    divider: SLATE[300],
    mutedLight: SLATE[400],
    muted: SLATE[500],
    slate: SLATE[600],
    body: SLATE[700]
  },
  SLATE,
  EMERALD,
  ROSE,
  RUBY,
  HONEY,
  SEMANTIC,
  /**
   * @deprecated Prefer HONEY.* / SEMANTIC.warning.
   * Mapped onto the honey ramp so existing GOLD.* call sites pick up app colors.
   */
  GOLD: {
    gold1: HONEY[50],
    gold2: HONEY[100],
    gold3: HONEY[200],
    gold4: HONEY[300],
    gold5: HONEY[400],
    gold6: HONEY[500],
    gold7: HONEY[700],
    gold8: HONEY[800],
    gold9: HONEY[900],
    gold10: HONEY[900]
  },
  /**
   * @deprecated Prefer EMERALD.* / SEMANTIC.positive.
   * Mapped onto the emerald ramp so existing GREEN.* call sites pick up app colors.
   */
  GREEN: {
    green1: EMERALD[50],
    green2: EMERALD[100],
    green3: EMERALD[200],
    green4: EMERALD[300],
    green5: EMERALD[400],
    green6: EMERALD[500],
    green7: EMERALD[700],
    green8: EMERALD[800],
    green9: EMERALD[900],
    green10: EMERALD[900]
  }
};
